import { ActionCardState, BookingStepName } from "types/bookingv2/booking"
import { IFinalEvent } from "types/event"
import { calculateTicketAndGroupStatuses } from "utils/helpers/bookingv2/bookingHelpers"
import { isTimeslotDisabled } from "./ticket"
import { isEidPartyEvent } from  "utils/helpers/constants"

/**
 * This file purpose is to check whether to enable the action card button for the current state of the current step or not.
 *
 * Ex: If a user was in SELECT_TICKETS step, the action card button should be disabled if the user didn't select any ticket.
 */

/**
 * Helper functions to determine if action card buttons should be enabled
 * based on the current booking step state
 */

/**
 * Checks if the selected day step requirements are met
 */
const checkEligibilityOfSelectDayStep = (
    state: ActionCardState,
    event?: IFinalEvent
): boolean => {
    const { cartState } = state
    if (!cartState.selectedDays) {
        return false
    }

    // For multi-slot days we intentionally store an empty timeslot_id placeholder.
    // In that case simply having a selected day is enough to proceed to Select Time.
    if (!cartState.selectedDays.timeslot_id) {
        return true
    }

    // Find the selected timeslot from the event data using the timeslot_id from the cart
    const selectedTimeslot = event?.time_slots.find(
        (slot) => slot._id === cartState.selectedDays?.timeslot_id
    )

    if (!selectedTimeslot) {
        // If event data is available but the timeslot cannot be matched (e.g. stale
        // cart after a refresh or back-navigation), still allow proceeding as long as
        // the cart has a selected day.  This covers the case where the stored
        // timeslot_id came from a filtered/processed copy of the slot list whose _id
        // format differs from the raw event.time_slots array.
        return true
    }

    // Use the isTimeslotDisabled logic to determine eligibility
    return !isTimeslotDisabled(selectedTimeslot)
}

/**
 * Checks if the select time step requirements are met
 */
const checkEligibilityOfSelectTimeStep = (
    state: ActionCardState,
    event?: IFinalEvent
): boolean => {
    const { cartState } = state
    const timeslotId = cartState.selectedDays?.timeslot_id

    if (!timeslotId) {
        return false
    }

    if (!event) {
        return true
    }

    const selectedTimeslot = event.time_slots.find(
        (slot) => slot._id === timeslotId
    )

    if (!selectedTimeslot) {
        return true
    }

    return !isTimeslotDisabled(selectedTimeslot)
}

/**
 * Checks if the selected tickets step requirements are met
 */
const checkEligibilityOfSelectTicketsStep = (
    state: ActionCardState,
    dynamicTranslate: (key: string, params?: Record<string, any>) => string,
    event?: IFinalEvent
): boolean => {
    const { cartState } = state
    const currentSelectedTickets = cartState.selectedTickets || [] // Actually selected tickets (ISelectedTicket)

    // We must have a selected timeslot and at least one selected ticket
    const timeslotId = cartState.selectedDays?.timeslot_id
    if (!timeslotId || !currentSelectedTickets.length || !event) {
        return false
    }

    const selectedTimeslot = event.time_slots.find(
        (slot) => slot._id === timeslotId
    )

    if (!selectedTimeslot) {
        return false
    }

    // Build the available ticket type definitions from the event data,
    // matching the structure used in useTicketProcessing so that
    // type_id values line up with cartState.selectedTickets.
    const availableTicketTypes = (selectedTimeslot.types || []).map(
        (type: any) => {
            const id =
                typeof type.type_id === "string"
                    ? type.type_id
                    : type.type_id?._id || ""

            const ticketDetails = event.availableTickets.find(
                (t) => t._id === id
            ) as any

            return {
                _id: id,
                type_id: id,
                ar_description: ticketDetails?.ar_description ?? "",
                en_description: ticketDetails?.en_description ?? "",
                en_title: ticketDetails?.en_title ?? "",
                ar_title: ticketDetails?.ar_title ?? "",
                color: ticketDetails?.color ?? "#ffffff",
                discount: ticketDetails?.discount ?? {
                    current_value: 0,
                    previous_value: 0,
                },
                // Quantities from time_slots.types
                limit: typeof type.limit === "number" ? type.limit : 0,
                // Min/max from availableTickets
                max: ticketDetails?.max ?? 0,
                min: ticketDetails?.min ?? 1,
                must_select_seat: ticketDetails?.must_select_seat ?? false,
                price: ticketDetails?.price ?? 0,
                group: ticketDetails?.group,
                wait_list:
                    typeof type.wait_list === "number" ? type.wait_list : 0,
            }
        }
    )

    if (!availableTicketTypes.length) {
        return false
    }

    // No need to map availableTicketTypes further, calculateTicketAndGroupStatuses handles it.
    //         return acc;
    //     },
    //     {} as Record<string, (typeof currentSelectedTickets)[0]>
    // );

    // const mappedTickets = availableTicketTypes.map((ticket) => {
    //     const selectedTicket = selectedTicketsMap[ticket._id];
    //     return {
    //         ...ticket,
    //     };
    // });

    const { isPass, isFail } = calculateTicketAndGroupStatuses(
        availableTicketTypes as any, // Pass the full definitions of available types
        currentSelectedTickets, // Pass the current cart's selected tickets
        dynamicTranslate,
        timeslotId
    )

    // Return true if validation passed and no failures
    const result = isPass && !isFail // CONDITION B
    return result
}

/**
 * Checks if the selected seat step requirements are met
 */
const checkEligibilityOfSelectSeatStep = (state: ActionCardState): boolean => {
    const { cartState } = state
    const selectedTickets = cartState.selectedTickets || []
    const selectedSeats = cartState.selectedSeats || []

    const eidPartyEvent = isEidPartyEvent(cartState.event)

    // For EID party events, enable next button if any seats are selected
    if (eidPartyEvent) {
        return selectedSeats.length > 0
    }

    // For other events: if any ticket requires seat selection, ensure at least
    // one seat is selected. We don't strictly enforce one seat per ticket
    // to keep the UX simple, only that seat selection is not empty.
    const hasMustSelectSeatTicket = selectedTickets.some(
        (ticket) => ticket.must_select_seat
    )

    if (hasMustSelectSeatTicket) {
        return selectedSeats.length > 0
    }

    // If no tickets require seats (but we are on this step due to display_seat_map),
    // then seat selection is optional
    return true
}

/**
 * Checks if the add-ons step requirements are met
 */
const checkEligibilityOfAddOnsStep = (_state: ActionCardState): boolean => {
    // Get event from context - we need to access the event's extras to validate requirements
    // Since we don't have direct access to the event here, we'll assume all required extras
    // are properly validated in the AddOns component itself.
    // For now, we'll enable the next button if there are no validation errors.
    // The AddOns component should handle the display of validation messages.

    // This is a simple validation - in a real scenario, you might want to pass
    // the event data through the ActionCardState or validate against specific requirements
    return true
}

type ValidationResult = {
    isValid: boolean
    errors: Record<string, string>
}

const validateAccreditation = (
    state: ActionCardState,
    event?: IFinalEvent
): ValidationResult => {
    const errors: Record<string, string> = {}
    const questions = event?.accreditation?.questions ?? []

    if (questions.length === 0) {
        return { isValid: true, errors }
    }

    const answers = state.cartState.accreditation ?? []
    const answerMap = new Map(
        answers.map((a) => [a.question_id, a.answer?.trim()])
    )

    for (const q of questions) {
        const answer = answerMap.get(q.question_id) ?? ""

        // 🔹 Required check
        if (q.is_required && !answer) {
            errors[q.question_id] = "This field is required"
            continue
        }

        // 🔹 Type-specific checks
        if (q.type === "email" && answer) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(answer)) {
                errors[q.question_id] = "Please enter a valid email"
            }
        }

        if (q.type === "phone_number" && answer) {
            let normalized = answer

            // normalize Saudi numbers
            if (normalized.startsWith("05")) {
                normalized = "966" + normalized.slice(1)
            } else if (normalized.startsWith("+966")) {
                normalized = normalized.slice(1)
            } else if (normalized.startsWith("00966")) {
                normalized = normalized.slice(2)
            }

            const phoneRegex = /^9665\d{8}$/ // final valid format
            if (!phoneRegex.test(normalized)) {
                errors[q.question_id] =
                    "Please enter a valid phone number (9665XXXXXXXX)"
            } else {
                // overwrite normalized value in state if needed
                answerMap.set(q.question_id, normalized)
            }
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    }
}

const checkEligibilityOfAccreditationStep = (
    state: ActionCardState,
    event?: IFinalEvent
): boolean => {
    const { isValid } = validateAccreditation(state, event)
    return isValid
    // const questions = event?.accreditation?.questions;
    // if (!questions || questions.length === 0) return true; // nothing to check

    // const requiredQuestions = questions.filter((q) => q.is_required);
    // if (requiredQuestions.length === 0) return true; // no required questions

    // const answers = state.cartState.accreditation ?? [];
    // const answerMap = new Map(
    //   answers.map((a) => [a.question_id, a.answer?.trim()])
    // );

    // return requiredQuestions.every((q) => Boolean(answerMap.get(q.question_id)));
}

/**
 * Checks if the checkout step requirements are met
 */
const checkEligibilityOfCheckoutStep = (
    state: ActionCardState,
    routePath: string
): boolean => {
    const { isTermsAgreementChecked } = state
    const isPreviewRoute = routePath.includes("preview")

    return !isPreviewRoute && Boolean(isTermsAgreementChecked)
}

/**
 * Determines if the action card button should be enabled based on the current step
 * @param state Current state of the action card and booking process
 * @param routePath Current route path
 * @param dynamicTranslate Translation function for dynamic messages
 * @returns Whether the action card button should be enabled
 */
export const isActionCardEnabled = (
    state: ActionCardState,
    routePath: string,
    dynamicTranslate: (key: string, params?: Record<string, any>) => string,
    event?: IFinalEvent
): boolean => {
    const { currentStepName } = state

    switch (currentStepName) {
        case BookingStepName.SELECT_DAY:
            return checkEligibilityOfSelectDayStep(state, event)

        case BookingStepName.SELECT_TIME:
            return checkEligibilityOfSelectTimeStep(state, event)

        case BookingStepName.SELECT_TICKETS:
            return checkEligibilityOfSelectTicketsStep(
                state,
                dynamicTranslate,
                event
            )

        case BookingStepName.SELECT_SEAT:
            return checkEligibilityOfSelectSeatStep(state)

        case BookingStepName.ADD_ONS:
            return checkEligibilityOfAddOnsStep(state)

        case BookingStepName.ACCREDITATION:
            return checkEligibilityOfAccreditationStep(state, event)

        case BookingStepName.CHECKOUT:
            return checkEligibilityOfCheckoutStep(state, routePath)

        default:
            return true
    }
}
